import {z} from '@hono/zod-openapi';

import {VoiceTenantLanguageSchema} from './VoiceTenantLanguageSchema';
import {VoiceUsageNormalizedTokenCountsSchema} from './VoiceUsageNormalizedTokenCountsSchema';

export const VOICE_CALL_COMPLETED_EVENT_SCHEMA_VERSION = '1.0.0';

const VoiceCallRejectedReasonSchema = z.enum([
  'no-context',
  'context-expired',
  'context-mismatch',
  'replay',
  'unknown-webhook',
  'capacity',
]);

export type VoiceCallRejectedReason = z.infer<typeof VoiceCallRejectedReasonSchema>;

/**
 * Voice Gateway -> DMS. Published to `VOICE_CALL_RESULT_TOPIC` once per call, on its terminal
 * outcome: a normal completion, a rejected admission (no matching `VoiceCallContextRegisteredEvent`
 * before expiry or timeout, replay, or an untrusted webhook), or a failure.
 *
 * `callReference` is `null` when the `IncomingVoiceCallCommand` that started the call carried
 * none; every other correlation field (`tenantId`, `workspace`, `language`, `scenarioId`) is then
 * `null` too, because the Gateway never had a registered context to read them from.
 *
 * `usage` reuses `VoiceUsageNormalizedTokenCountsSchema`, not `VoiceUsageObservationSchema`:
 * the observation schema is keyed to one Realtime `responseId` and does not fit a whole-call
 * total. Until whole-call usage aggregation exists, this carries the token counts of the call's
 * last observed response, and is `null` when the call produced no response (for example a
 * `rejected` outcome).
 *
 * Transcript delivery is out of this version — see the handover doc's "what remains open".
 */
export const VoiceCallCompletedEventSchema = z
  .object({
    schemaVersion: z.literal(VOICE_CALL_COMPLETED_EVENT_SCHEMA_VERSION),
    eventType: z.literal('voice.call.completed'),
    callReference: z.string().uuid().nullable(),
    callId: z.string().min(1),
    conversationId: z.string().min(1).nullable(),
    tenantId: z.string().min(1).nullable(),
    workspace: z.string().min(1).nullable(),
    language: VoiceTenantLanguageSchema.nullable(),
    scenarioId: z.string().min(1).nullable(),
    outcome: z.discriminatedUnion('kind', [
      z
        .object({
          kind: z.literal('completed'),
          terminalOutcome: z.string().min(1),
          businessOutcome: z.string().min(1).nullable(),
        })
        .strict(),
      z
        .object({
          kind: z.literal('rejected'),
          reason: VoiceCallRejectedReasonSchema,
        })
        .strict(),
      z
        .object({
          kind: z.literal('failed'),
          reason: z.string().min(1),
        })
        .strict(),
    ]),
    verifiedReferences: z.array(
      z
        .object({
          kind: z.enum(['customer', 'vehicle']),
          id: z.string().min(1),
        })
        .strict()
    ),
    usage: VoiceUsageNormalizedTokenCountsSchema.nullable(),
    startedAt: z.string().datetime({offset: true}).nullable(),
    endedAt: z.string().datetime({offset: true}),
  })
  .strict()
  .openapi('VoiceCallCompletedEventSchema');

export type VoiceCallCompletedEvent = z.infer<typeof VoiceCallCompletedEventSchema>;
